import { useMutation, useQueryClient, type UseMutationResult } from '@tanstack/react-query';

import { ApiError, apiClient, fleetPath } from '@/lib/api-client';

import { fleetKeys } from './fleet-keys';

/** SPEC-0019 — body for POST /servers/provision-vm (snake_case, mirrors ProvisionVmDto). */
export interface ProvisionVmBody {
  name: string;
  provider: 'upcloud';
  role: 'exit_node' | 'bootstrap_edge';
  zone: string;
  plan?: '1xCPU-1GB' | '1xCPU-2GB' | '2xCPU-4GB';
  capabilities?: string[];
  enable_floating_ip?: boolean;
}

export interface ProvisionVmResult {
  task_id: number;
  status_url: string;
}

// The backend's `backend/api/src/modules/fleet/upcloud-zones.ts` is the authority
// for country/city (and continent) — this list only supplies form labels and
// must be kept in sync with it by hand.
export const UPCLOUD_ZONES: ReadonlyArray<{ id: string; label: string; country: string }> = [
  { id: 'au-syd1', label: 'Sydney', country: 'AU' },
  { id: 'de-fra1', label: 'Frankfurt', country: 'DE' },
  { id: 'dk-cph1', label: 'Copenhagen', country: 'DK' },
  { id: 'es-mad1', label: 'Madrid', country: 'ES' },
  { id: 'fi-hel1', label: 'Helsinki 1', country: 'FI' },
  { id: 'fi-hel2', label: 'Helsinki 2', country: 'FI' },
  { id: 'nl-ams1', label: 'Amsterdam', country: 'NL' },
  { id: 'no-svg1', label: 'Stavanger', country: 'NO' },
  { id: 'pl-waw1', label: 'Warsaw', country: 'PL' },
  { id: 'se-sto1', label: 'Stockholm', country: 'SE' },
  { id: 'sg-sin1', label: 'Singapore', country: 'SG' },
  { id: 'uk-lon1', label: 'London', country: 'GB' },
  { id: 'us-chi1', label: 'Chicago', country: 'US' },
  { id: 'us-nyc1', label: 'New York', country: 'US' },
  { id: 'us-sjo1', label: 'San Jose', country: 'US' },
];

/** POST /servers/provision-vm — the VM does not exist until the task's callback lands. */
export function useProvisionVm(): UseMutationResult<ProvisionVmResult, ApiError, ProvisionVmBody> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body) => apiClient.post<ProvisionVmResult>(fleetPath('/servers/provision-vm'), body),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: fleetKeys.all });
    },
  });
}
